import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/utils/api";
import { toast } from "sonner";
import { Eye, Search, RefreshCw, ArrowLeft, Send } from "lucide-react";
import {
  StatusPill, SlaIndicator, StatsRow, MessageBubble,
} from "@/components/phase4/Phase4Common";

const DISPUTE_TYPES = [
  "WEIGHT_DISCREPANCY", "DAMAGE_IN_TRANSIT", "WRONG_DELIVERY",
  "DELAYED_DELIVERY", "MISSING_ITEM", "BILLING_ERROR",
  "SERVICE_FAILURE", "OTHER",
];

const RESOLVE_STATUSES = [
  "RESOLVED_FAVOUR_CUSTOMER", "RESOLVED_FAVOUR_SENDTOO", "CLOSED",
];

export default function DisputesInbox() {
  const { id } = useParams();
  if (id) return <DisputeDetail id={id} />;
  return <DisputesList />;
}

function DisputesList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterBreached, setFilterBreached] = useState(false);
  const [stats, setStats] = useState({ open: 0, review: 0, breach: 0, resolved_week: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: 1, limit: 100 };
      if (filterStatus) params.status = filterStatus;
      if (filterType) params.dispute_type = filterType;
      if (filterBreached) params.sla_breached = true;
      const r = await api.get("/v2/disputes", { params });
      setItems(r.data?.items || []);
      const all = await api.get("/v2/disputes", { params: { limit: 1000 } });
      const allItems = all.data?.items || [];
      const weekAgo = new Date(Date.now() - 7 * 86400 * 1000);
      setStats({
        open: allItems.filter((d) => d.status === "OPEN").length,
        review: allItems.filter((d) => d.status === "UNDER_REVIEW").length,
        breach: allItems.filter((d) => d.sla_breach).length,
        resolved_week: allItems.filter((d) => {
          if (!d.resolved_at) return false;
          return new Date(d.resolved_at) >= weekAgo;
        }).length,
      });
    } catch (e) {
      toast.error("Failed to load disputes");
    }
    setLoading(false);
  }, [filterStatus, filterType, filterBreached]);

  useEffect(() => { load(); }, [load]);

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="disputes-inbox">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase">Disputes Inbox</h1>
            <p className="text-slate-500 text-sm mt-0.5">Customer dispute management</p>
          </div>
          <button onClick={load} className="px-3 py-1.5 border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-semibold rounded inline-flex items-center gap-1">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>

        <StatsRow items={[
          { key: "open", label: "Open", value: stats.open, color: "text-red-600" },
          { key: "review", label: "Under Review", value: stats.review, color: "text-blue-600" },
          { key: "breach", label: "SLA Breached", value: stats.breach, color: "text-rose-600" },
          { key: "resolved-week", label: "Resolved (7d)", value: stats.resolved_week, color: "text-emerald-600" },
        ]} />

        <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap gap-2 items-end">
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Status</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
              data-testid="dispute-filter-status"
              className="text-xs border border-slate-200 rounded px-2 py-1.5">
              <option value="">ALL</option>
              {["OPEN", "UNDER_REVIEW", "AWAITING_CUSTOMER", "AWAITING_PARTNER", "RESOLVED_FAVOUR_CUSTOMER", "RESOLVED_FAVOUR_SENDTOO", "CLOSED", "ESCALATED"].map((s) =>
                <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Type</label>
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
              className="text-xs border border-slate-200 rounded px-2 py-1.5">
              <option value="">ALL</option>
              {DISPUTE_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-1 text-xs text-slate-600">
            <input type="checkbox" checked={filterBreached} onChange={(e) => setFilterBreached(e.target.checked)}
              data-testid="dispute-filter-breached" />
            SLA Breached only
          </label>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="disputes-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["Dispute ID", "AWB", "Customer", "Type", "Claimed ₹", "Status", "SLA", "Assigned", "Actions"].map((h) =>
                  <th key={h} className="text-left px-3 py-2 text-[10px] font-bold text-slate-500 uppercase">{h}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? <tr><td colSpan={9} className="text-center py-8 text-slate-400">Loading…</td></tr>
                : items.length === 0 ? <tr><td colSpan={9} className="text-center py-8 text-slate-400">No disputes.</td></tr>
                : items.map((d) => (
                  <tr key={d.dispute_id} className="hover:bg-slate-50" data-testid={`dispute-row-${d.dispute_id}`}>
                    <td className="px-3 py-2 font-mono text-xs">{d.dispute_id}</td>
                    <td className="px-3 py-2 font-mono text-xs">{d.awb || "—"}</td>
                    <td className="px-3 py-2 text-xs">{d.customer_id?.slice(-6) || "—"}</td>
                    <td className="px-3 py-2 text-xs">{(d.dispute_type || "").replace(/_/g, " ")}</td>
                    <td className="px-3 py-2 text-xs font-semibold">₹{(d.claimed_amount || 0).toFixed(0)}</td>
                    <td className="px-3 py-2"><StatusPill status={d.status} /></td>
                    <td className="px-3 py-2"><SlaIndicator deadline={d.sla_deadline} breached={d.sla_breach} resolved={d.resolved_at} /></td>
                    <td className="px-3 py-2 text-[11px] text-slate-500">{d.assigned_to?.slice(-6) || "—"}</td>
                    <td className="px-3 py-2">
                      <button onClick={() => navigate(`/admin/disputes/${d.dispute_id}`)}
                        data-testid={`dispute-view-${d.dispute_id}`}
                        className="text-brand-500 hover:text-brand-700">
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}

function DisputeDetail({ id }) {
  const navigate = useNavigate();
  const [dispute, setDispute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [internal, setInternal] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [assignTo, setAssignTo] = useState("");
  const [resolveStatus, setResolveStatus] = useState("");
  const [resolveText, setResolveText] = useState("");
  const [refund, setRefund] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/v2/disputes/${id}`);
      setDispute(r.data);
    } catch {
      toast.error("Failed to load dispute");
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/users", { params: { role: "staff", limit: 100 } })
      .then((r) => setStaffList(r.data?.items || r.data || []))
      .catch(() => {});
  }, []);

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post(`/v2/disputes/${id}/message`, { content: reply, is_internal: internal });
      toast.success("Reply sent");
      setReply(""); setInternal(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    setBusy(false);
  };

  const doAssign = async () => {
    if (!assignTo) return;
    try {
      await api.put(`/v2/disputes/${id}/assign`, { assigned_to: assignTo });
      toast.success("Assigned");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const doResolve = async () => {
    if (!resolveStatus) { toast.error("Select status"); return; }
    setBusy(true);
    try {
      await api.put(`/v2/disputes/${id}/resolve`, {
        status: resolveStatus,
        resolution: resolveText,
        resolution_amount: resolveStatus === "RESOLVED_FAVOUR_CUSTOMER" ? parseFloat(refund || 0) : 0,
      });
      toast.success("Dispute resolved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  if (loading || !dispute) {
    return <DashboardLayout><div className="p-8 text-slate-400">Loading dispute…</div></DashboardLayout>;
  }
  const isResolved = ["RESOLVED_FAVOUR_CUSTOMER", "RESOLVED_FAVOUR_SENDTOO", "CLOSED"].includes(dispute.status);

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="dispute-detail-page">
        <button onClick={() => navigate("/admin/disputes")} className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Back to inbox
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left: info + thread */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border bg-slate-100 text-slate-700">
                  {(dispute.dispute_type || "").replace(/_/g, " ")}
                </span>
                <StatusPill status={dispute.status} />
                <SlaIndicator deadline={dispute.sla_deadline} breached={dispute.sla_breach} resolved={dispute.resolved_at} />
              </div>
              <h2 className="font-heading text-lg font-bold text-slate-900">Dispute {dispute.dispute_id}</h2>
              <div className="grid grid-cols-2 gap-y-1 mt-3 text-xs">
                <div className="text-slate-500">AWB</div><div className="font-mono">{dispute.awb || "—"}</div>
                <div className="text-slate-500">LR</div><div className="font-mono">{dispute.lr_number || "—"}</div>
                <div className="text-slate-500">Claimed</div><div className="font-semibold">₹{(dispute.claimed_amount || 0).toFixed(2)}</div>
                <div className="text-slate-500">SLA Deadline</div><div>{dispute.sla_deadline ? new Date(dispute.sla_deadline).toLocaleString() : "—"}</div>
              </div>
              <div className="mt-3 text-xs">
                <div className="text-slate-500 mb-1">Description</div>
                <div className="bg-slate-50 rounded p-2 text-slate-800 whitespace-pre-wrap">{dispute.description}</div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <h3 className="font-heading text-sm font-bold text-slate-900 mb-3">Discussion</h3>
              <div className="space-y-2 max-h-[400px] overflow-y-auto" data-testid="dispute-thread">
                {(dispute.messages || []).length === 0 ? (
                  <div className="text-xs text-slate-400 text-center py-4">No messages yet</div>
                ) : (dispute.messages || []).map((m, i) => <MessageBubble key={m.message_id || i} msg={m} />)}
              </div>
              <div className="mt-3 border-t pt-3 space-y-2">
                <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={3}
                  placeholder="Reply…"
                  data-testid="dispute-reply-input"
                  className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" />
                <div className="flex items-center justify-between">
                  <label className="text-xs text-slate-600 flex items-center gap-1">
                    <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)}
                      data-testid="dispute-reply-internal" />
                    Internal note (staff only)
                  </label>
                  <button onClick={sendReply} disabled={busy || !reply.trim()}
                    data-testid="dispute-reply-send"
                    className="px-3 py-1.5 bg-brand-500 hover:bg-brand-700 text-white text-xs font-semibold rounded disabled:opacity-50 inline-flex items-center gap-1">
                    <Send className="w-3.5 h-3.5" /> Send Reply
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Right: action panel */}
          <div className="space-y-3">
            <div className="bg-white border border-slate-200 rounded-lg p-3">
              <h4 className="text-[10px] font-bold uppercase text-slate-500 mb-2">Assign</h4>
              <div className="space-y-2">
                <select value={assignTo} onChange={(e) => setAssignTo(e.target.value)}
                  data-testid="dispute-assign-select"
                  className="w-full text-xs border border-slate-200 rounded px-2 py-1.5">
                  <option value="">— Select staff —</option>
                  {staffList.map((s) => <option key={s.id || s._id} value={s.id || s._id}>{s.name || s.email}</option>)}
                </select>
                <button onClick={doAssign} disabled={!assignTo}
                  data-testid="dispute-assign-btn"
                  className="w-full px-3 py-1.5 bg-slate-700 hover:bg-slate-900 text-white text-xs font-semibold rounded disabled:opacity-50">
                  Assign
                </button>
              </div>
            </div>

            {!isResolved && (
              <div className="bg-white border border-slate-200 rounded-lg p-3">
                <h4 className="text-[10px] font-bold uppercase text-slate-500 mb-2">Resolve</h4>
                <div className="space-y-2">
                  <select value={resolveStatus} onChange={(e) => setResolveStatus(e.target.value)}
                    data-testid="dispute-resolve-status"
                    className="w-full text-xs border border-slate-200 rounded px-2 py-1.5">
                    <option value="">— Select outcome —</option>
                    {RESOLVE_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                  </select>
                  <textarea value={resolveText} onChange={(e) => setResolveText(e.target.value)} rows={3}
                    placeholder="Resolution notes…"
                    data-testid="dispute-resolve-text"
                    className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" />
                  {resolveStatus === "RESOLVED_FAVOUR_CUSTOMER" && (
                    <div>
                      <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Refund Amount (₹)</label>
                      <input type="number" min="0" step="0.01" value={refund} onChange={(e) => setRefund(e.target.value)}
                        data-testid="dispute-refund-amount"
                        className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" />
                      <p className="text-[10px] text-slate-500 mt-1 italic">Amount will be credited to customer wallet immediately</p>
                    </div>
                  )}
                  <button onClick={doResolve} disabled={busy || !resolveStatus}
                    data-testid="dispute-resolve-btn"
                    className="w-full px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded disabled:opacity-50">
                    Resolve Dispute
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
